/**
 * 🔒 HTML SANITIZER UTILITY
 * Protects against XSS attacks by sanitizing HTML before rendering
 */

import DOMPurify from 'dompurify';

// Configuration for AI-generated HTML (allows more formatting)
const AI_HTML_CONFIG = {
  ALLOWED_TAGS: ['h2', 'h3', 'h4', 'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'a', 'blockquote'],
  ALLOWED_ATTR: ['href', 'title', 'target'],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):)/i,
  KEEP_CONTENT: true,
  RETURN_DOM_FRAGMENT: false,
  RETURN_DOM: false,
  RETURN_TRUSTED_TYPE: false
};

// Configuration for user input (stricter)
const USER_INPUT_CONFIG = {
  ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'ul', 'ol', 'li'],
  ALLOWED_ATTR: [],
  KEEP_CONTENT: true
};

// Configuration for plain text (removes all HTML)
const PLAIN_TEXT_CONFIG = {
  ALLOWED_TAGS: [],
  ALLOWED_ATTR: [],
  KEEP_CONTENT: true
};

/**
 * Sanitize AI-generated HTML
 * Allows rich formatting (headings, lists, links, etc.)
 * 
 * @param {string} dirtyHTML - Untrusted HTML from AI
 * @returns {string} Safe HTML
 */
export const sanitizeAIHTML = (dirtyHTML) => {
  if (!dirtyHTML) return '';
  
  return DOMPurify.sanitize(dirtyHTML, AI_HTML_CONFIG);
};

/**
 * Sanitize user-generated HTML
 * Only allows basic formatting (bold, italic, lists)
 * 
 * @param {string} dirtyHTML - Untrusted HTML from user
 * @returns {string} Safe HTML
 */
export const sanitizeUserHTML = (dirtyHTML) => {
  if (!dirtyHTML) return '';
  
  return DOMPurify.sanitize(dirtyHTML, USER_INPUT_CONFIG);
};

/**
 * Escape all HTML to plain text
 * Removes all tags and converts to safe text
 * 
 * @param {string} text - Text that may contain HTML
 * @returns {string} Plain text
 */
export const escapeHTML = (text) => {
  if (!text) return '';
  
  return DOMPurify.sanitize(text, PLAIN_TEXT_CONFIG);
};

/**
 * Sanitize HTML with custom configuration
 * 
 * @param {string} dirtyHTML - Untrusted HTML
 * @param {object} config - Custom DOMPurify config
 * @returns {string} Safe HTML
 */
export const sanitizeHTML = (dirtyHTML, config = {}) => {
  if (!dirtyHTML) return '';
  
  const finalConfig = { ...AI_HTML_CONFIG, ...config };
  return DOMPurify.sanitize(dirtyHTML, finalConfig);
};

/**
 * Check if content contains potentially dangerous HTML
 * 
 * @param {string} html - HTML to check
 * @returns {boolean} True if potentially dangerous
 */
export const isDangerousHTML = (html) => {
  if (!html) return false;
  
  const dangerous = ['<script', '<iframe', 'javascript:', 'onerror=', 'onclick='];
  const lowerHTML = html.toLowerCase();
  
  return dangerous.some(pattern => lowerHTML.includes(pattern));
};

// Hook for React components
export default {
  sanitizeAIHTML,
  sanitizeUserHTML,
  escapeHTML,
  sanitizeHTML,
  isDangerousHTML
};
